// Now — who is on the clock, what is due and untouched, who finished.
// GET /api/dispatch/now every 30s and on focus; the elapsed column ticks
// locally between answers. The only red on the screen is a late job.
//
// ── The clock has to look ALIVE (2026-09-10) ───────────────────────────────
//
// The owner: "Timer running should show seconds, so we can see timer is
// actually running." The interval below has run once a second since this
// screen shipped, and the column rendered `formatClock` — H:MM. So the
// number changed once a MINUTE and every re-render in between drew the same
// characters, which is what a dead page looks like from the other side of
// the screen.
//
// A running clock is a `BigClock` now, at `sm` (19px H:MM, the size the row
// already printed, plus two small characters of seconds). A FINISHED shift
// stays `formatClock`: it is a total, not a timer, and a second hand on a
// number that cannot change is noise.
//
// It is fed `seconds` rather than `startedAt`, so the local tick stays
// anchored on the SERVER's `elapsed_seconds` via `elapsedNow` — a phone whose
// own clock is wrong still reads the right shift length. No new polling: this
// is the same 30s request and the same one interval for the whole list.
//
// ── WHERE, not just who (2026-09-09) ───────────────────────────────────────
//
// The owner's complaint, in their words: "admins don't see the location
// collected on the jobs." docs/PARITY_2026-09-08.md §7 — this screen held the
// GPS fix and did not show it. `clocked_in[].lat` / `.lng` are the fix the
// crew phone took at the clock-in (apps/field/geo.js → routes/field.js, then
// `te.latitude AS lat, te.longitude AS lng` in routes/dispatch.js); the only
// thing that ever read them was the tap-through sheet, so a dispatcher
// scanning eight rows had to open eight sheets to learn that seven of them had
// no fix at all. It is on the ROW now.
//
// And `due_not_started[].address` — routes/dispatch.js `shapeJob` builds a
// full "street, city" for every job on this screen and nothing drew it, so
// "who is where" answered with a property NAME. That is the difference
// between recognising a place and being able to go to it.
//
// Three deliberate shapes, all of them carried from the Office job panel that
// answered the same complaint on 2026-09-06 (office/records/Jobs.js
// `or-entries`), because that one was measured in a browser at 320px:
//
//   · the coordinates sit BESIDE the link, never inside its label — with them
//     in the label the anchor rendered 246px wide inside a 320px screen
//   · an entry with no fix says so in words. Most historic rows have none, and
//     a blank space where a link sits on the row above reads as a broken
//     screen rather than as an absent measurement
//   · how FAR the fix is from the property is deliberately not claimed:
//     `properties` carries no coordinate columns (server/database.js), so
//     there is nothing to measure against. A number would have to be invented.
//
// ── A MAP of it, and a way through to the job (2026-09-10) ─────────────────
//
// The owner: "Could we have a map in Right Now tab that shows workers
// location, by default zoom in to see all workers and on finished today jobs,
// make them clickable so we can go directly to the job."
//
// Three things, and each of them is a property of this file rather than of a
// new screen:
//
//   · `<WorkerMap>` is handed `onSite` — the SAME `clocked_in` array the rows
//     below are drawn from. It fetches nothing, so it adds no permission of
//     its own (the gate stays `requireDispatch` on GET /api/dispatch/now) and
//     nothing to the /api/ rate budget a whole crew shares. Default zoom fits
//     every pin; see apps/shared/tileMap.js `fitView`.
//   · A pin opens THIS sheet, so there is one place a person is described and
//     one link out to a real map, rather than a second popup with a second
//     spelling of the same three facts.
//   · Finished rows navigate to the job record, and rows with no job stay
//     inert — see jobLink.js for why a dead link is worse here than no link.
//
// What the map does NOT claim is the load-bearing part: a fix is written once,
// at clock-in, and never updated. Every label says "clocked in", never "is".
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PersonRow, Pill, Stat, SectionLabel, Sheet, Notice, Skeleton, BigClock, formatClock, initialsOf, FwButton, FwField, FwInput, FwTextarea } from '../../../ui/fw';
import { describeError, post } from '../../shared/http';
import { useCan } from '../../../hooks/usePermission';
import { useAuth } from '../../../App';
import { UNASSIGNED_LABEL } from '../../shared/jobFields';
import { gpsText, mapHref, accuracyText } from '../../shared/geoFix';
import { jobRecordHref } from '../../shared/jobLink';
import WorkerMap from './WorkerMap';
import { fetchLiveClock, LIVE_CLOCK_KEY } from '../../shared/liveClock';
import { usePageChrome } from '../../shell/chrome';
import { dayLong, elapsedNow, clockTime } from '../lib/format';

// How many Behind rows are drawn before the rest become a count. Eight is
// roughly one phone screenful of `.dp-onsite` pairs at 390px, which keeps
// "On site now" — the thing this screen is named after — reachable without
// scrolling past somebody else's backlog.
const BEHIND_MAX = 8;

const list = (a) => (Array.isArray(a) ? a : []);
const placeOf = (r) => [r.property_name, r.job_title].filter(Boolean).join(' · ') || 'No job';

/**
 * The location line under a row: the link, the reading beside it, or the
 * absence stated.
 *
 * A link cannot go INSIDE the row: PersonRow renders a <button> when it is
 * tappable, and an anchor inside a button is not a thing a browser can be
 * trusted with. So the row keeps its sheet and this sits under it.
 */
function WhereFix({ entry }) {
  const href = mapHref(entry);
  return (
    <div className="dp-where" data-where={href ? 'fix' : 'none'}>
      {href ? (
        <>
          <a className="dp-where__map" href={href} target="_blank" rel="noopener noreferrer">
            Open in maps
          </a>
          <span className="dp-where__coords">{gpsText(entry)}</span>
        </>
      ) : (
        <span className="dp-where__none">No location on this clock-in</span>
      )}
    </div>
  );
}

/**
 * A meta line from parts, dropping the ones the server did not send.
 *
 * `.filter(Boolean)` before the join is the whole point: `address` is null for
 * a job with no property and `ended_at` is null for a row with no end time, and
 * joining a null straight in leaves a dangling " · " that reads as a rendering
 * fault rather than as missing data.
 */
const metaLine = (...parts) => parts.filter(Boolean).join(' · ');


/**
 * When the finished shift actually ended (§22), leading the line for the same
 * truncation reason — and because `finished_today` is `ORDER BY ended_at DESC`,
 * so the time is what the list is arranged by.
 *
 * "ended" is said out loud so the reading cannot be mistaken for a start time.
 * `clockTime` returns '' for a null or unparseable value, which `metaLine`
 * then drops.
 */
const endedMeta = (p) => {
  const at = clockTime(p.ended_at);
  return metaLine(at ? `ended ${at}` : null, placeOf(p));
};

/**
 * ── What is actually BEHIND (2026-09-12) ────────────────────────────────────
 *
 * The stat at the top of this screen read "LATE 11" out of eleven jobs due
 * today, in red, measured in Chromium at 390px. The server's entire definition
 * was `hourNow >= 9`, so after nine in the morning every unstarted job was
 * late — and the jobs that were genuinely behind were on no screen at all,
 * because the list that flag rode on only holds work whose window covers
 * TODAY.
 *
 * `late` now means the last day a job was scheduled for has passed and nobody
 * has finished it (server/routes/dispatch.js `isJobLate`), `counts.late` is
 * the length of the `overdue` list, and this is that list. A red number with
 * nothing behind it is the same dead end the old one was: the point of the
 * count is the work it names.
 *
 * Rendered only when there IS something behind. An empty urgent section above
 * the day's work is noise, and the stat already says 0 in a neutral tone —
 * which on a healthy week is every day.
 */
const daysLateLabel = (n) => {
  const days = Number(n);
  if (!Number.isFinite(days) || days <= 0) return 'Late';
  return `${days} ${days === 1 ? 'day' : 'days'} late`;
};

/**
 * Whether anybody is on a late job right now — the difference between "behind
 * and being dealt with" and "behind and abandoned". Read off `on_clock`, which
 * the server builds with the SAME function that fills `clocked_in` above.
 */
const liveNote = (j) => {
  const on = list(j.on_clock);
  if (!on.length) return 'Nobody on it';
  const first = on[0] || {};
  if (!first.name) return 'Somebody on the clock';
  return on.length === 1 ? `${first.name} on the clock` : `${first.name} +${on.length - 1} on the clock`;
};

/**
 * A Date as the value an `<input type="datetime-local">` wants: local wall
 * clock, no zone, to the minute.
 *
 * Built by hand rather than via toISOString().slice(), which is UTC — on this
 * company's clock that is four or five hours out, so the box would open
 * showing tomorrow morning for a shift that ended this afternoon.
 */
export function localInputValue(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function Now() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // The same marker the route checks (server/routes/timeEntries.js
  // hasClockOversight), so the button is not offered to somebody the server
  // will answer 403 — the "ghost button" usePermission.js warns about.
  const canManageClocks = useCan('time_entries', 'manage_all');
  const canManageTeam = useCan('time_entries', 'manage_team');
  const mayStopOthers = canManageClocks || canManageTeam;
  // WHOSE clock is open. `liveShifts` (server/routes/dispatch.js) selects every
  // active user with `end_time IS NULL` and does not exclude the caller, so a
  // manager who is clocked in appears in this list like anyone else — and the
  // button was offered on their own row. Pressing it always failed: `doStop`
  // sends an end_time, and the route refuses an end_time for your OWN clock
  // ("Your own clock stops now, not at a time you choose"). A dead button, on
  // the one row its owner is most likely to tap. Raised in review.
  //
  // Your own clock is stopped from the Field screen, where you are standing.
  const { user } = useAuth();
  const canStop = (p) => mayStopOthers && !!p && p.user_id !== (user && user.id);
  // The shift being closed, the end time being given for it, and why.
  const [stopping, setStopping] = useState(null);
  const [stopAt, setStopAt] = useState('');
  const [stopWhy, setStopWhy] = useState('');
  const [stopBusy, setStopBusy] = useState(false);
  const [stopError, setStopError] = useState(null);

  const beginStop = (p) => {
    // The person sheet CLOSES. Without this both Sheets are open at once, each
    // with aria-modal and each registering a document-level Escape handler —
    // and Sheet.js uses stopPropagation, which does nothing for a second
    // listener on the same node, so one Escape closed both. The comment below
    // says a separate sheet was chosen so backdrops would not stack; leaving
    // this one open produced the stack anyway.
    //
    // It also matters AFTER a successful stop: the person sheet would be
    // revealed again with the clock still ticking and a live "Stop this clock"
    // for an entry that is now closed, whose second press 400s "already
    // stopped". Raised in review.
    setOpen(null);
    setStopping(p);
    setStopAt(localInputValue(new Date()));
    setStopWhy('');
    setStopError(null);
  };

  const doStop = async () => {
    if (!stopping) return;
    setStopBusy(true);
    setStopError(null);
    try {
      // `end_time` is sent ALWAYS, even when it reads "now". The whole reason
      // this exists is the forgotten clock — noticed on Monday, started on
      // Friday — and a stop with no end time books every hour in between.
      // Making the field explicit and pre-filled means the office states the
      // end rather than inheriting it.
      const when = new Date(stopAt);
      if (Number.isNaN(when.getTime())) { setStopError('That is not a time we can read.'); return; }
      await post(`/time-entries/stop/${stopping.entry_id}`, {
        end_time: when.toISOString(),
        pause_note: stopWhy.trim() || undefined,
      });
      setStopping(null);
      qc.invalidateQueries({ queryKey: LIVE_CLOCK_KEY });
    } catch (e) {
      // The server's own sentence — "That end time is before the shift
      // started." names what to change; a generic one does not.
      setStopError(describeError(e, 'That did not stop.'));
    } finally {
      setStopBusy(false);
    }
  };

  const q = useQuery({
    queryKey: LIVE_CLOCK_KEY,
    queryFn: fetchLiveClock,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(null);

  const data = q.data;
  // Empty title on purpose — see Board.js. The menu row reads "Right now" and
  // this header read "Now", so the screen renamed itself between the tap and
  // the arrival. shell/chrome.js falls back to navigation.js's label for an
  // empty title, so there is now one name for this screen and the menu owns
  // it. The eyebrow stays ours: "Live · Thu 10 Sep" is what the menu cannot say.
  usePageChrome('', data && data.today ? `Live · ${dayLong(data.today)}` : 'Live');
  const onSite = list(data && data.clocked_in);
  const due = list(data && data.due_not_started);
  // A key added 2026-09-12. `list()` answers [] for a server that predates it,
  // so an older build and a newer one — or the reverse — show no section
  // rather than an exception on a dispatcher's screen.
  const overdue = list(data && data.overdue);
  // ── The Behind list has a ceiling (2026-09-13) ────────────────────────────
  //
  // This section is drawn FIRST, above the map and above "On site now", and
  // it was `overdue.map(...)` with nothing bounding it. The server's overdue
  // SELECT has no LIMIT — it returns every job with a past scheduled day that
  // was never closed — and nothing mechanically closes one: spawnNextOccurrence
  // runs on completion, so a recurring job nobody completed leaves its parents
  // open forever. The justification in this file was "it is also normally
  // absent", which is an assumption about the data rather than a bound on it.
  //
  // That is the defect this app was rebuilt to remove, in the same shape: on
  // the crew's Today screen, eight billing notices put the day's one action at
  // y=1260 in an 844px viewport. A list that comes FIRST is the one that has
  // to be bounded. The rows arrive worst-first, so the ones kept are the ones
  // a dispatcher would have scrolled to find, and the rest are COUNTED rather
  // than dropped — a dispatcher who cannot see that forty jobs are behind will
  // not go looking for them.
  const overdueShown = overdue.slice(0, BEHIND_MAX);
  const overdueRest = overdue.length - overdueShown.length;
  const finished = list(data && data.finished_today);
  const ticking = onSite.length > 0;

  useEffect(() => {
    if (!ticking) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ticking]);

  const counts = (data && data.counts) || {};
  const late = counts.late || 0;

  return (
    <>
      {q.isPending ? <Skeleton lines={6} /> : null}
      {q.error ? <Notice tone="stop" className="dp-notice">{describeError(q.error)}</Notice> : null}
      {data ? (
        <>
          <div className="dp-stats">
            <Stat label="On clock" value={counts.on_clock || 0} tone={counts.on_clock ? 'now' : 'neutral'} />
            <Stat label="Late" value={late} tone={late > 0 ? 'stop' : 'neutral'} />
            <Stat label="Finished" value={counts.finished || 0} tone="ok" />
          </div>

          {/* Behind FIRST, above everything, because it is the only thing on
              this screen that is already going wrong. It is also normally
              absent — see the note on daysLateLabel above. */}
          {overdue.length ? (
            <>
              <SectionLabel>Behind</SectionLabel>
              <div className="dp-list" data-section="late">
                {overdueShown.map((j) => {
                  const who = list(j.assignees).map((a) => a.name).filter(Boolean);
                  const href = jobRecordHref(j.id);
                  return (
                    <div className="dp-onsite" key={j.id}>
                      <PersonRow
                        tone="stop"
                        initials={who.length ? initialsOf(who[0]) : '?'}
                        name={j.title}
                        /* The PLACE is left off whenever the line below is
                           carrying it. Measured at 390px on 2026-09-12:
                           "Whitfield Residence · Nobody on it · Ana Silva"
                           rendered as "Whitfield Residence · …" — PersonRow's
                           meta is one nowrap line, so the property name ate
                           the row and the two facts that are the reason this
                           section exists were both past the ellipsis. The
                           address under it is the better half of that pair
                           anyway: it is somewhere a crew can be sent. When
                           there is no address, the place comes back, because
                           then nothing else on the row says where. */
                        meta={[
                          j.address ? '' : (j.property_name || j.client_name),
                          liveNote(j),
                          who.length ? who.join(', ') : UNASSIGNED_LABEL,
                        ].filter(Boolean).join(' · ')}
                        trailing={<Pill tone="stop">{daysLateLabel(j.days_late)}</Pill>}
                        onClick={href ? () => navigate(href) : undefined}
                        data-late="true"
                        data-job-link={href || undefined}
                        data-crew={who.length ? 'assigned' : 'unassigned'}
                      />
                      <div className="dp-where">
                        <span className={j.address ? 'dp-where__addr' : 'dp-where__none'}>
                          {j.address || 'No address on file'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {overdueRest > 0 ? (
                <div className="dp-more" data-late-more={overdueRest}>
                  {overdueRest} more behind, {overdue.length} in total. The board lists every one.
                </div>
              ) : null}
            </>
          ) : null}

          {/* The map goes ABOVE the list it is about, and is fed the same
              `clocked_in` array the rows below are drawn from — no request of
              its own, so no new permission and nothing added to the /api/
              budget a whole crew shares. A pin opens the same sheet the row
              does. See WorkerMap.js. */}
          <WorkerMap entries={onSite} onOpen={setOpen} />

          <SectionLabel>On site now</SectionLabel>
          <div className="dp-list" data-section="on-site">
            {onSite.length === 0 ? <div className="dp-quiet">Nobody on the clock.</div> : null}
            {onSite.map((p) => (
              <div className="dp-onsite" key={p.entry_id || `${p.user_id}-${p.job_id}`}>
                <PersonRow
                  tone="now"
                  initials={p.initials || initialsOf(p.name)}
                  name={p.name}
                  meta={placeOf(p)}
                  trailing={<BigClock size="sm" seconds={elapsedNow(p.elapsed_seconds, data.received_at, now)} />}
                  onClick={() => setOpen(p)}
                />
                <WhereFix entry={p} />
              </div>
            ))}
          </div>

          <SectionLabel>Not started</SectionLabel>
          <div className="dp-list" data-section="not-started">
            {due.length === 0 ? <div className="dp-quiet">Everything due today has someone on it.</div> : null}
            {due.map((j) => {
              const who = list(j.assignees).map((a) => a.name).filter(Boolean);
              // "Unassigned", in words. This read `who.join(', ')` inside a
              // `.filter(Boolean)`, so a job NOBODY is on rendered the same
              // meta line as one three people are on, minus the names — and
              // "nobody has been given this yet" is the single thing a
              // dispatcher opens Right now to find. An absence is not a
              // signal; it looks like the screen failed to draw a name.
              const crew = who.length ? who.join(', ') : UNASSIGNED_LABEL;
              return (
                <div className="dp-onsite" key={j.id}>
                  <PersonRow
                    tone={j.late ? 'stop' : 'sched'}
                    /* An unassigned job gets no INITIALS. The avatar is a
                       person-shaped badge, and filling it from the property
                       name put "HI" for Hillcrest where a crew member's
                       initials go — which reads as somebody rather than as
                       nobody. `?` is what Avatar already shows for a name it
                       cannot read. */
                    initials={who.length ? initialsOf(who[0]) : '?'}
                    /* The JOB, not the place. `property_name || title` meant
                       the title — what the work actually is — was invisible on
                       every job that has a property, which is most of them.
                       The place moved to the meta line below, where it sits
                       beside who is on it. */
                    name={j.title}
                    meta={[j.property_name || j.client_name, 'Nobody clocked in', crew].filter(Boolean).join(' · ')}
                    trailing={<Pill tone={j.late ? 'stop' : 'sched'}>{j.late ? 'Late' : 'Due'}</Pill>}
                    data-late={j.late ? 'true' : 'false'}
                    data-crew={who.length ? 'assigned' : 'unassigned'}
                  />
                  {/* Where to send somebody. `address` has been on this
                      response since the screen shipped and was never drawn;
                      a property name is not a place you can drive to. */}
                  <div className="dp-where">
                    <span className={j.address ? 'dp-where__addr' : 'dp-where__none'}>
                      {j.address || 'No address on file'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <SectionLabel>Finished today</SectionLabel>
          <div className="dp-list" data-section="finished">
            {finished.length === 0 ? <div className="dp-quiet">Nothing wrapped up yet.</div> : null}
            {/* "on finished today jobs, make them clickable so we can go
                directly to the job" — the owner, 2026-09-10.

                A row with a job navigates; a row WITHOUT one does not become a
                button at all. `finished_today` LEFT JOINs jobs
                (server/routes/dispatch.js), so `job_id` is null for an entry
                whose job has been deleted, and a button that navigates to a
                list which then opens nothing is indistinguishable from a
                screen ignoring taps — see jobLink.js. PersonRow renders a
                <button> only when it is given an onClick, so withholding it is
                the whole mechanism. */}
            {finished.map((p) => {
              const href = jobRecordHref(p.job_id);
              return (
                <PersonRow
                  key={`${p.user_id}-${p.job_id}`}
                  className="dp-dim"
                  tone="ok"
                  initials={p.initials || initialsOf(p.name)}
                  name={p.name}
                  meta={endedMeta(p)}
                  trailing={formatClock(p.duration_seconds)}
                  onClick={href ? () => navigate(href) : undefined}
                  data-job-link={href || undefined}
                />
              );
            })}
          </div>
        </>
      ) : null}

      <Sheet open={!!open} title={open ? open.name : ''} onClose={() => setOpen(null)}>
        {open ? (
          <div className="dp-sheet-body">
            <dl>
              <div className="dp-kv"><dt>Job</dt><dd>{placeOf(open)}</dd></div>
              <div className="dp-kv"><dt>Started</dt><dd>{clockTime(open.started_at) || '—'}</dd></div>
              <div className="dp-kv"><dt>On the clock</dt><dd><BigClock size="md" seconds={elapsedNow(open.elapsed_seconds, data && data.received_at, now)} /></dd></div>
              {/* The reading itself, not only a button that hides it. A
                  dispatcher reading a position out to somebody on the phone
                  needs the numbers; a link cannot be read aloud. */}
              {/* "Clocked in at", never "is at". This sheet is also what a
                  MAP PIN opens, and a pin is the one place in the app where a
                  stale fix can be mistaken for a live one — so the word that
                  fixes it belongs next to the numbers. accuracyText is '' on
                  every row today (time_entries has no accuracy column yet), in
                  which case the sentence says "approximate" instead of
                  claiming a figure it does not have. */}
              <div className="dp-kv">
                <dt>Clocked in at</dt>
                <dd className="fw-num">
                  {gpsText(open) || 'No location recorded'}
                  {gpsText(open) ? ` · ${accuracyText(open) || 'approximate'}` : ''}
                </dd>
              </div>
            </dl>
            {/*
              `mapHref`, not `open.lat != null`: an empty string is `!= null`,
              so the old guard built `?q=,` for an entry whose coordinates came
              back as ''. geoFix answers with Number.isFinite on both, which is
              the same question every other screen asks about the same columns.
            */}
            {mapHref(open) ? (
              <a
                className="fw-btn fw-btn--ghost fw-btn--md fw-btn--full"
                href={mapHref(open)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in maps
              </a>
            ) : (
              <div className="dp-quiet">No location on this clock-in.</div>
            )}
            {/* The other half of "go directly to the job": a pin, and the row
                it belongs to, both open this sheet, and this is the way out of
                it into the record. Same guard as the finished rows — no job,
                no button (jobLink.js). */}
            {jobRecordHref(open.job_id) ? (
              <button
                type="button"
                className="fw-btn fw-btn--ghost fw-btn--md fw-btn--full"
                data-job-link={jobRecordHref(open.job_id)}
                onClick={() => { const to = jobRecordHref(open.job_id); setOpen(null); navigate(to); }}
              >
                Open job
              </button>
            ) : null}
            {/* ── Stop a clock somebody forgot ──────────────────────────────
                Owner, 2026-09-18: "need admin to be able to stop timers, in
                case workers forget to stop it." Deliberately HERE and not on
                the row: stopping somebody's clock writes hours into payroll,
                and one stray tap while scrolling a list of eight people is not
                how that should happen. From here the dispatcher has already
                chosen a person and can see when they started. */}
            {canStop(open) ? (
              <FwButton
                variant="stop"
                full
                data-stop-clock={open.entry_id}
                onClick={() => beginStop(open)}
              >
                Stop this clock
              </FwButton>
            ) : null}
          </div>
        ) : null}
      </Sheet>

      {/* ── The confirm, which is where the END TIME is stated ───────────────
          A separate sheet rather than a nested one: two backdrops stack and
          the inner one traps focus against the outer, which on a phone reads
          as a screen that will not close.

          The end time is a FIELD, pre-filled with now, because the forgotten
          clock is the whole reason this exists. Clocked in Friday 5pm, noticed
          Monday 9am: accept "now" and 64 hours go to the timesheet and then to
          payroll. Asking makes the office state the end rather than inherit
          it, and the server bounds it on both sides. */}
      <Sheet
        open={!!stopping}
        title={stopping ? `Stop ${stopping.name}'s clock` : ''}
        onClose={() => setStopping(null)}
        footer={(
          <>
            <FwButton variant="quiet" onClick={() => setStopping(null)} disabled={stopBusy}>Cancel</FwButton>
            <FwButton
              variant="stop"
              onClick={doStop}
              loading={stopBusy}
              disabled={!stopAt}
              data-stop-confirm
            >
              Stop the clock
            </FwButton>
          </>
        )}
      >
        {stopping ? (
          <div className="dp-sheet-body">
            {stopError ? <Notice tone="stop" onDismiss={() => setStopError(null)}>{stopError}</Notice> : null}
            <dl>
              <div className="dp-kv"><dt>Job</dt><dd>{placeOf(stopping)}</dd></div>
              <div className="dp-kv"><dt>Started</dt><dd>{clockTime(stopping.started_at) || '—'}</dd></div>
            </dl>
            <FwField
              label="When did they finish?"
              htmlFor="dp-stop-at"
              required
              hint="Set this to when the work actually stopped, not when you noticed. It is what they are paid for."
            >
              <FwInput
                id="dp-stop-at"
                type="datetime-local"
                value={stopAt}
                onChange={(e) => setStopAt(e.target.value)}
              />
            </FwField>
            <FwField label="Why are you stopping it" htmlFor="dp-stop-why">
              <FwTextarea
                id="dp-stop-why"
                value={stopWhy}
                onChange={(e) => setStopWhy(e.target.value)}
                rows={3}
                placeholder="Forgot to clock out"
              />
            </FwField>
            {/* Said plainly, because the row carries it either way: the server
                prefixes the entry's note with who stopped it.

                NOT "so the worker will see it" — review checked, and
                `pause_note` is rendered on no Field or Dispatch screen today.
                Its only readers are the office reports export. The record is
                real and this sentence is honest about it; surfacing it on the
                worker's own Hours screen is a separate change. */}
            <div className="fw-field__hint">Your name is recorded on the entry.</div>
          </div>
        ) : null}
      </Sheet>
    </>
  );
}
